import { normalizePrincipalFormValue } from '../utils/principalTypes';
import { normalizePluginsFormValue } from '../utils/pluginConfig';
import POLICY_SCHEMA from '../metadata/policySchema.json';
import POLICY_CATEGORIES from '../metadata/policyCategories.json';

export { POLICY_SCHEMA, POLICY_CATEGORIES };

export const POLICY_VERSION = POLICY_SCHEMA.policyVersion;

export function getCategoryById(categoryId) {
  return POLICY_CATEGORIES.find((c) => c.id === categoryId) || POLICY_CATEGORIES[0];
}

export function getTemplateById(categoryId, templateId) {
  const category = getCategoryById(categoryId);
  return category?.templates?.find((t) => t.id === templateId) || null;
}

export function getConditionTemplate(templateId) {
  return POLICY_SCHEMA.conditionTemplates[templateId] || null;
}

export function getFieldDef(fieldKey) {
  return POLICY_SCHEMA.fields[fieldKey] || null;
}

export function getMergedFieldValidation(fieldKey, categoryId) {
  const field = getFieldDef(fieldKey);
  const category = getCategoryById(categoryId);
  const base = { ...(field?.validation || {}) };
  const overrides = category?.validationOverrides?.[fieldKey] || {};
  return { ...base, ...overrides };
}

export function getMergedFieldRules(fieldKey, conditionTemplateId) {
  const field = getFieldDef(fieldKey);
  const template = getConditionTemplate(conditionTemplateId);
  const templateRules = template?.fieldRules?.[fieldKey] || {};
  return { ...field, ...templateRules };
}

export function getActiveConditionFields(conditionTemplateId) {
  const template = getConditionTemplate(conditionTemplateId);
  if (!template?.fields) return [];
  return template.fields
    .map((key) => getMergedFieldRules(key, conditionTemplateId))
    .filter(Boolean);
}

export function getAllowedConditionTemplates(categoryId) {
  const category = getCategoryById(categoryId);
  const allowed = category?.allowedConditionTemplates;
  if (!allowed?.length) return Object.keys(POLICY_SCHEMA.conditionTemplates);
  return allowed.filter((id) => POLICY_SCHEMA.conditionTemplates[id]);
}

export function getAllBuiltinTemplates() {
  return POLICY_CATEGORIES.flatMap((category) =>
    (category.templates || []).map((template) => ({
      ...template,
      categoryId: category.id,
      categoryLabel: category.label,
      categoryColor: category.color,
      categoryBg: category.bg,
      categoryAccent: category.accent,
      categoryIcon: category.icon,
      isCustom: false,
    })),
  );
}

export const STATEMENT_FIELD_KEYS = [
  'sid',
  'effect',
  'action',
  'resource',
  'principal',
  'conditionTemplate',
  'fact',
  'operator',
  'value',
  'valueMin',
  'valueMax',
  'approvalRole',
  'plugins',
];

export function createStatementKey() {
  return `stmt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function arrayFieldToFormValue(value) {
  if (!value || value === '*') return '*';
  if (Array.isArray(value)) {
    if (!value.length) return '';
    if (value.length === 1 && value[0] === '*') return '*';
    return value.join(', ');
  }
  return String(value);
}

/** Normalize condition to rule list — supports IAM { Op: { fact: val } } and legacy { all: [...] }. */
function conditionRulesFromStatement(statement) {
  const condition = statement?.condition;
  if (!condition || typeof condition !== 'object') return [];
  if (Array.isArray(condition.all)) return condition.all;

  const rules = [];
  Object.entries(condition).forEach(([operator, factMap]) => {
    if (!factMap || typeof factMap !== 'object' || Array.isArray(factMap)) return;
    Object.entries(factMap).forEach(([fact, value]) => {
      rules.push({ fact, operator, value });
    });
  });
  return rules;
}

function conditionTemplateFromStatement(statement) {
  if (!statement?.condition) return 'none';
  const rules = conditionRulesFromStatement(statement);
  if (!rules.length) return 'none';
  if (
    rules.length === 2
    && rules.every((rule) => rule.fact === rules[0].fact)
    && rules.some((rule) => rule.operator === 'NumericGreaterThan')
    && rules.some((rule) => rule.operator === 'NumericLessThan')
  ) {
    return 'timeRange';
  }
  const rule = rules[0];
  if (['between', 'not_between'].includes(rule.operator)) {
    if (/hour|time|system\.hour/i.test(String(rule.fact || ''))) return 'timeRange';
    return 'threshold';
  }
  if (rule.operator === 'Bool') return 'pattern';
  if (Array.isArray(rule.value)) {
    if (rule.value.length === 2 && rule.value.every((v) => typeof v === 'number' || !Number.isNaN(Number(v)))) {
      return 'threshold';
    }
    return 'list';
  }
  if (['TenantEquals', 'TenantMatch'].includes(rule.operator)) return 'tenant';
  if (['Contains', 'NotContains'].includes(rule.operator)
    && String(rule.fact || '').toLowerCase().includes('tenant')) {
    return 'tenant';
  }
  if (['Contains', 'NotContains'].includes(rule.operator)) return 'pattern';
  if (['NumericGreaterThan', 'NumericLessThan'].includes(rule.operator)) return 'threshold';
  if (['RoleEquals', 'RoleContains'].includes(rule.operator)) return 'role';
  if (['GroupEquals', 'GroupContains'].includes(rule.operator)) return 'group';
  return 'pattern';
}

function conditionFieldsFromStatement(statement) {
  const conditionTemplate = conditionTemplateFromStatement(statement);
  if (conditionTemplate === 'none') {
    return { conditionTemplate: 'none', effect: statement.effect === 'Allow' ? 'Allow' : statement.effect };
  }

  const rules = conditionRulesFromStatement(statement);
  if (conditionTemplate === 'timeRange') {
    const betweenRule = rules.find((rule) => ['between', 'not_between'].includes(rule.operator));
    if (betweenRule && Array.isArray(betweenRule.value) && betweenRule.value.length >= 2) {
      return {
        conditionTemplate,
        fact: betweenRule.fact || 'system.hour',
        valueMin: Number(betweenRule.value[0]),
        valueMax: Number(betweenRule.value[1]),
      };
    }
    const minRule = rules.find((rule) => rule.operator === 'NumericGreaterThan');
    const maxRule = rules.find((rule) => rule.operator === 'NumericLessThan');
    const minValue = Number(minRule?.value);
    const maxValue = Number(maxRule?.value);
    return {
      conditionTemplate,
      fact: minRule?.fact || maxRule?.fact || 'system.hour',
      valueMin: Number.isFinite(minValue) ? minValue + 1 : 0,
      valueMax: Number.isFinite(maxValue) ? maxValue - 1 : 23,
    };
  }

  const rule = rules[0] || {};
  if (['between', 'not_between'].includes(rule.operator) && Array.isArray(rule.value) && rule.value.length >= 2) {
    return {
      conditionTemplate,
      fact: rule.fact || '',
      operator: rule.operator,
      valueMin: rule.value[0],
      valueMax: rule.value[1],
      value: '',
    };
  }
  return {
    conditionTemplate,
    fact: rule.fact || '',
    operator: rule.operator || 'StringEquals',
    value: Array.isArray(rule.value) ? rule.value.join(', ') : String(rule.value ?? ''),
  };
}

export function statementFromPolicyDocument(statement) {
  const conditionFields = conditionFieldsFromStatement(statement);
  const approvalRole = statement.approval?.role
    || (Array.isArray(statement.approval?.approvers) ? statement.approval.approvers[0] : '')
    || '';
  return buildInitialStatementValues({
    sid: statement.sid || '',
    effect: statement.effect || 'Deny',
    action: arrayFieldToFormValue(statement.action),
    resource: arrayFieldToFormValue(statement.resource),
    principal: normalizePrincipalFormValue(statement.principal),
    approvalRole,
    plugins: normalizePluginsFormValue(statement.plugins),
    threat: statement.threat || undefined,
    ...conditionFields,
  });
}

export function buildInitialStatementValues(overrides = {}) {
  const categoryId = overrides.categoryId || POLICY_CATEGORIES[0]?.id || 'security';
  const conditionTemplate = overrides.conditionTemplate
    || getAllowedConditionTemplates(categoryId)[0]
    || 'pattern';
  const values = {};
  Object.values(POLICY_SCHEMA.fields).forEach((field) => {
    if (!STATEMENT_FIELD_KEYS.includes(field.key)) return;
    if (field.defaultValue !== undefined) {
      values[field.key] = field.defaultValue;
    } else if (field.type === 'principalList' || field.type === 'principalMap') {
      values[field.key] = [];
    } else if (field.type === 'pluginList') {
      values[field.key] = [];
    } else if (field.type === 'toggleGroup' && field.options?.length) {
      values[field.key] = field.options[0].value;
    } else {
      values[field.key] = '';
    }
  });
  values.conditionTemplate = conditionTemplate;
  const templateDefaults = getConditionTemplate(conditionTemplate)?.defaults || {};
  return {
    _id: overrides._id || createStatementKey(),
    ...values,
    ...templateDefaults,
    ...overrides,
    principal: normalizePrincipalFormValue(overrides.principal ?? values.principal),
    plugins: normalizePluginsFormValue(overrides.plugins ?? values.plugins),
    approvalRole: overrides.approvalRole ?? values.approvalRole ?? '',
    valueMin: overrides.valueMin ?? templateDefaults.valueMin ?? values.valueMin ?? 0,
    valueMax: overrides.valueMax ?? templateDefaults.valueMax ?? values.valueMax ?? 23,
  };
}

export function getPolicyStatements(formValues) {
  if (Array.isArray(formValues?.statements) && formValues.statements.length > 0) {
    return formValues.statements.map((statement) => buildInitialStatementValues({
      categoryId: formValues?.categoryId,
      ...statement,
      _id: statement._id || createStatementKey(),
    }));
  }

  const migrated = buildInitialStatementValues({ categoryId: formValues?.categoryId });
  STATEMENT_FIELD_KEYS.forEach((key) => {
    if (formValues?.[key] !== undefined && formValues[key] !== null && formValues[key] !== '') {
      migrated[key] = formValues[key];
    }
  });
  if (formValues?.conditionTemplate) {
    migrated.conditionTemplate = formValues.conditionTemplate;
    const templateDefaults = getConditionTemplate(formValues.conditionTemplate)?.defaults || {};
    Object.assign(migrated, templateDefaults);
    STATEMENT_FIELD_KEYS.forEach((key) => {
      if (formValues?.[key] !== undefined && formValues[key] !== null && formValues[key] !== '') {
        migrated[key] = formValues[key];
      }
    });
  }
  migrated.principal = normalizePrincipalFormValue(migrated.principal);
  migrated.plugins = normalizePluginsFormValue(migrated.plugins);
  return [migrated];
}

export function buildInitialFormValues(overrides = {}) {
  const { statements: statementOverrides, ...rest } = overrides;
  const categoryId = rest.categoryId || POLICY_CATEGORIES[0]?.id || 'security';
  const conditionTemplate = rest.conditionTemplate
    || getAllowedConditionTemplates(categoryId)[0]
    || 'pattern';
  const statementSeed = { categoryId, conditionTemplate, ...rest };
  const statements = Array.isArray(statementOverrides) && statementOverrides.length
    ? statementOverrides.map((statement) => buildInitialStatementValues({ categoryId, ...statement }))
    : [buildInitialStatementValues(statementSeed)];

  return {
    label: rest.label ?? '',
    description: rest.description ?? '',
    categoryId,
    statements,
  };
}

export function buildFormValuesFromPolicy(policy) {
  if (!policy) return buildInitialFormValues();

  const defaults = policy.defaults || policy.formValues || {};
  const categoryId = policy.categoryId || defaults.categoryId || 'security';

  if (Array.isArray(defaults.statements) && defaults.statements.length > 0) {
    return buildInitialFormValues({
      label: policy.label || defaults.label || '',
      description: policy.description || defaults.description || '',
      categoryId,
      statements: defaults.statements.map((statement) => buildInitialStatementValues({
        categoryId,
        ...statement,
        principal: normalizePrincipalFormValue(statement.principal),
        plugins: normalizePluginsFormValue(statement.plugins),
      })),
    });
  }

  const documentStatements = policy.policyDocument?.statements;
  if (Array.isArray(documentStatements) && documentStatements.length > 0) {
    return buildInitialFormValues({
      label: policy.label || '',
      description: policy.description || '',
      categoryId,
      statements: documentStatements.map((statement) => statementFromPolicyDocument(statement)),
    });
  }

  return buildInitialFormValues({
    label: policy.label || '',
    description: policy.description || '',
    categoryId,
    conditionTemplate: policy.conditionTemplate || policy.formType || defaults.conditionTemplate || 'pattern',
    ...defaults,
    principal: normalizePrincipalFormValue(defaults.principal),
    plugins: normalizePluginsFormValue(defaults.plugins),
    approvalRole: defaults.approvalRole || '',
    valueMin: defaults.valueMin ?? 0,
    valueMax: defaults.valueMax ?? 23,
  });
}

export function isFieldVisible(field, formValues) {
  if (!field?.visibleWhen) return true;
  return Object.entries(field.visibleWhen).every(([key, expected]) => {
    const actual = formValues[key];
    if (Array.isArray(expected)) return expected.includes(actual);
    return actual === expected;
  });
}

export function resolveFieldLabel(field, conditionTemplateId) {
  if (field.dynamicLabel?.[conditionTemplateId]) {
    return field.dynamicLabel[conditionTemplateId];
  }
  return field.label;
}

export function resolveOperatorOptions(conditionTemplateId) {
  const all = POLICY_SCHEMA.operators || [];
  const allowed = conditionTemplateId
    ? getConditionTemplate(conditionTemplateId)?.fieldRules?.operator?.options
    : null;
  const filtered = allowed?.length
    ? all.filter((op) => allowed.includes(op.value))
    : all;
  return filtered;
}

export const RELATIONSHIP_OPERATORS = ['RelationshipExists', 'RelationshipNotExists'];
export const NUMERIC_OPERATORS = ['NumericGreaterThan', 'NumericLessThan'];
export const RANGE_OPERATORS = ['between', 'not_between'];

export function operatorNeedsValue(operator) {
  return !RELATIONSHIP_OPERATORS.includes(operator);
}
